import type { Container, Spritesheet, Texture } from "pixi.js";
import type { SlotApp } from "../app/createSlotApp";
import type { StageLayers } from "./createStage";
import type { BootLaunchPayload } from "./BootLaunchPayload";
import type { CoreServices } from "./CoreServices";
import type { DeferredFeatureHudPort } from "../feature-hud/DeferredFeatureHudPort";
import type { GameBackgroundDefinition } from "../composition/GameDefinition";
import { loadShellUiAssets } from "../assets/ShellUiAssets";
import { Graphics } from "pixi.js";
import { CONTROL_BAR_LABELS, CONTROL_BAR_TAG, defineControlBarElement, type ControlBarElement } from "../controls/bar/ControlBarElement";
import type { ControlBarLayout } from "../controls/bar/portraitBarLayout";
import type { AutoSpinPopupPort } from "../autospin/AutoSpinPorts";
import type { ShellMenuPort } from "../controls/menu/ShellMenuPorts";
import type { SpeedModePopupPort } from "../controls/speed-mode/SpeedModePorts";
import { GAME_HEADER_TAG, defineGameHeaderElement, type GameHeaderElement } from "../controls/header/GameHeaderElement";
import { buildChromeArt, loadTitleControlArt, type ChromeControlArtSpec } from "../chrome/chromeArt";
import { localizeLabels } from "../chrome/chromeLabels";
import { FEATURE_PURCHASE_LABELS, FEATURE_PURCHASE_TAG, defineFeaturePurchaseElement, type FeaturePurchaseElement } from "../feature-purchase/FeaturePurchaseElement";
import { GAME_HISTORY_LABELS, GAME_HISTORY_TAG, defineGameHistoryElement, type GameHistoryElement } from "../game-history/GameHistoryElement";
import { INTRO_SPLASH_LABELS, INTRO_SPLASH_TAG, defineIntroSplashElement, type IntroSplashElement } from "../intro/IntroSplashElement";
import type { IntroSplashCardIcon } from "../intro/IntroSplashSpec";
import { createChromeRoot, type ChromeTheme } from "../chrome/createChromeRoot";
import { SYSTEM_MODAL_TAG, defineSystemModalElement, type SystemModalElement } from "../system-modal/SystemModalElement";
import { SystemModalPresenter } from "../system-modal/SystemModalPresenter";
import { GridLoadingOverlayView } from "../spin/loading-overlay/GridLoadingOverlayView";
import { ChromeImageResolver } from "../chrome/ChromeImageResolver";
import { INFO_PAGE_LABELS, INFO_PAGE_TAG, defineShellInfoPageElement, type ShellInfoPageElement } from "../controls/info/ShellInfoPageElement";
import { BrandedIconView, type GameLogoSpec } from "../branding/BrandedIconView";
import { SHELL_BUNDLE_NAMES } from "./defaults/BundleNames";
import type { Log, PixiPresentationTicker, ViewportStore } from "@slot/presentation";

/** The shell chrome, built: the control panel and header, the shell modals, the in-game logo, the grid loading overlay. */
export interface ShellChrome {
  /** The DOM control bar (the presenters' ports) and the DOM header strip. */
  controlBar: ControlBarElement;
  header: GameHeaderElement;
  /** The bar's own popovers (its children: at its toggle on desktop, full-screen on mobile): the settings menu, the autospin and speed pickers. */
  menu: ShellMenuPort;
  autoSpinPopup: AutoSpinPopupPort;
  speedPopup: SpeedModePopupPort;
  /** The full-screen tap-to-slam surface under the controls (Pixi, in the controls layer). */
  gridSlamOverlay: Graphics;
  /** The DOM intro splash (above every page, under the header), its art loading, and the card icons as image URLs. */
  introSplash: IntroSplashElement;
  introArtReady: Promise<void>;
  resolveIntroCardIcon: (icon: IntroSplashCardIcon) => Promise<string | null>;
  systemModalPresenter: SystemModalPresenter;
  gridLoadingOverlay: GridLoadingOverlayView;
  /** The scene-level DOM panels on the chrome root, handed to the control bar presenter. */
  infoPagePanel: ShellInfoPageElement;
  gameHistoryPanel: GameHistoryElement;
  featurePurchasePanel: FeaturePurchaseElement;
  /** The lazily rasterised images (the offer icons, the info page images); the chrome's own art is the atlas, placed by CSS. */
  images: ChromeImageResolver;
}

/**
 * The shell chrome around the game's scene: on the DOM chrome root, the header, the control bar with
 * its menu and pickers, feature purchase, info, history, the intro splash and the system modal
 * (decision 18); in the Pixi scene, the tap-to-slam surface in the controls layer, the title's logo
 * (when it declares one) and the grid loading overlay inside the composition root.
 */
export function createShellChrome(input: {
  slot: SlotApp;
  layers: StageLayers;
  payload: BootLaunchPayload;
  services: CoreServices;
  presentationTicker: PixiPresentationTicker;
  /** The control bar's feature HUD art target, created before the services it needs. */
  featureHud: DeferredFeatureHudPort;
  /** The title shows the bar's feature HUD (decision 55: one that shows its feature's progress itself does not). */
  showFeatureHud: boolean;
  /** The game's composition root: the logo goes below it, the loading overlay above its HUD. */
  compositionRoot: Container;
  compositionSize: { width: number; height: number };
  /** The title's in-game logo (`GameSceneHandle.logo`); null mounts none. */
  logo: GameLogoSpec | null;
  /** The title's background stills (decision 69): the intro splash shows them; absent, the boot bundle's `background-intro`. */
  background: GameBackgroundDefinition | undefined;
  theme: Partial<ChromeTheme> | undefined;
  /** The title's control art over the shell's (decision 49); undefined shows the shell's. */
  controlArt: ChromeControlArtSpec | undefined;
  /** The title's portrait arrangement of the bar (decision 78); undefined keeps the shell's places. */
  barLayout: ControlBarLayout | undefined;
  log: Log;
}): ShellChrome {
  const { slot, layers, payload, services, log } = input;

  // The title's in-game logo (below the grid): the LOCAL boot-assets logo — not operator-branded.
  if (input.logo) {
    const brandedIconView = new BrandedIconView({ logo: payload.assets.getLoadedAssetOrNull<Texture>(SHELL_BUNDLE_NAMES.bootAssets, "logo"), spec: input.logo, viewport: slot.viewport, bounds: input.compositionSize });
    input.compositionRoot.addChildAt(brandedIconView, 0);
  }

  const shellUi = loadShellUiAssets(payload.assets, log);
  // `GridSlamOverlay`: an invisible full-screen tap surface under every control.
  const gridSlamOverlay = new Graphics();
  gridSlamOverlay.label = "GridSlamOverlay";
  gridSlamOverlay.eventMode = "static";
  layers.controls.addChild(gridSlamOverlay);
  slot.viewport.subscribe((v) => gridSlamOverlay.clear().rect(-v.design.width / 2, -v.design.height / 2, v.design.width, v.design.height).fill({ color: 0x000000, alpha: 0 }));

  // The DOM chrome root (design units, scaled with the canvas) and the DOM panels on it.
  const chromeRoot = createChromeRoot({
    overlayRoot: slot.domOverlayRoot,
    viewport: slot.viewport,
    audio: { emit: (eventId) => services.audioService.presentation.emit({ id: eventId }) },
    haptics: services.hapticsService,
    theme: input.theme,
    pause: input.presentationTicker,
  });
  // The loading overlay is the composition root's LAST child at its origin (0, 0): the handle documents
  // that the root's origin is the grid's resting spot.
  const gridLoadingOverlay = new GridLoadingOverlayView({ frames: shellUi.loadingFrames, ticker: input.presentationTicker, log });
  input.compositionRoot.addChild(gridLoadingOverlay);

  // The scene-level DOM panels: info page, game history, feature purchase (in that scene order).
  const infoIcons = payload.assets.getLoadedAssetOrNull<Spritesheet>("shell-ui", "sprites/infoPage/icons");
  const images = new ChromeImageResolver({
    renderer: slot.app.renderer,
    branded: payload.brandedAssets,
    sharedBundleName: payload.sharedBundleName,
    inlineAtlases: new Map(infoIcons ? [["icons", infoIcons]] : []),
    log,
  });
  const t = (key: string, fallback: string) => services.localization.t(key, fallback);
  const mount = <T extends HTMLElement>(tag: string, define: () => void): T => {
    define();
    const element = document.createElement(tag) as unknown as T & { viewport?: ViewportStore | null };
    if ("viewport" in element) element.viewport = slot.viewport;
    chromeRoot.element.appendChild(element);
    return element;
  };
  // Mount order is paint order: the control bar (its popovers are its children) under the pages, then the
  // intro splash, then the system modal above every surface it can interrupt, then the header above all.
  const chromeArt = buildChromeArt(shellUi, input.controlArt ? loadTitleControlArt(payload.assets, input.controlArt, log) : null, log);
  const controlBar = mount<ControlBarElement>(CONTROL_BAR_TAG, defineControlBarElement);
  controlBar.art = chromeArt;
  controlBar.layout = input.barLayout ?? {};
  controlBar.labels = localizeLabels(CONTROL_BAR_LABELS, t);
  controlBar.holdToSpin = payload.initialSettings.holdToSpinEnabled !== false;
  controlBar.canvasOverlay = services.canvasOverlayStore;
  // A title without the HUD leaves the port without a target: its presenter still runs (the feature's block), unseen.
  if (input.showFeatureHud) input.featureHud.setTarget(controlBar.featureHud);
  const { menu, autoSpinPopup, speedPopup } = controlBar;
  const featurePurchasePanel = mount<FeaturePurchaseElement>(FEATURE_PURCHASE_TAG, defineFeaturePurchaseElement);
  featurePurchasePanel.art = chromeArt;
  featurePurchasePanel.labels = localizeLabels(FEATURE_PURCHASE_LABELS, t);
  const infoPagePanel = mount<ShellInfoPageElement>(INFO_PAGE_TAG, defineShellInfoPageElement);
  infoPagePanel.art = chromeArt;
  infoPagePanel.labels = localizeLabels(INFO_PAGE_LABELS, t);
  infoPagePanel.resolveImage = (src) => images.resolve(src);
  const gameHistoryPanel = mount<GameHistoryElement>(GAME_HISTORY_TAG, defineGameHistoryElement);
  gameHistoryPanel.art = chromeArt;
  gameHistoryPanel.labels = localizeLabels(GAME_HISTORY_LABELS, t);
  // The intro splash: default boot art (decision 5), the game's cards (decision 3), as image URLs.
  // Mounted BEFORE the system modal: mount order is paint order here, and a boot-boundary failure
  // raised while the splash is up would otherwise show its reload modal underneath the blackout
  // (review 2026-09-19, finding 50). It still covers the pages, which are mounted above.
  const introSplash = mount<IntroSplashElement>(INTRO_SPLASH_TAG, defineIntroSplashElement);
  introSplash.labels = localizeLabels(INTRO_SPLASH_LABELS, t);
  defineSystemModalElement();
  const systemModalElement = document.createElement(SYSTEM_MODAL_TAG) as SystemModalElement;
  chromeRoot.element.appendChild(systemModalElement);
  const systemModalPresenter = new SystemModalPresenter(systemModalElement, services.interactionBlockStore, services.hapticsService);
  systemModalPresenter.start();
  const toDataUrl = (texture: Texture | null): Promise<string | null> => (texture ? images.textureToDataUrl(texture) : Promise.resolve(null));
  // The boot bundle's art by URL, as the loader shows it (decision 69: nothing there decoded for the DOM); the card is a
  // frame of the shared bundle, loaded anyway.
  const bootUrl = (path: string): string | null => payload.assets.resolveUrl(SHELL_BUNDLE_NAMES.bootAssets, path);
  const stills = input.background?.stills;
  const introArtReady = toDataUrl(payload.assets.getLoadedAssetOrNull<Texture>(payload.sharedBundleName, "intro/intro_card")).then((card) => {
    introSplash.art = { background: bootUrl(stills?.landscape ?? "background-intro"), backgroundPortrait: stills ? bootUrl(stills.portrait) : null, logo: bootUrl("logo"), card };
  });
  const resolveIntroCardIcon = (icon: IntroSplashCardIcon): Promise<string | null> => toDataUrl(payload.assets.getLoadedAssetOrNull<Spritesheet>(payload.sharedBundleName, icon.sheet)?.textures[icon.frame] ?? null);
  const header = mount<GameHeaderElement>(GAME_HEADER_TAG, defineGameHeaderElement);

  return { controlBar, header, menu, autoSpinPopup, speedPopup, gridSlamOverlay, introSplash, introArtReady, resolveIntroCardIcon, systemModalPresenter, gridLoadingOverlay, infoPagePanel, gameHistoryPanel, featurePurchasePanel, images };
}
